import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import axios from 'axios'
import { useApiCredentials } from '@/composables/useApiCredentials'
import useWrapper from '@/composables/useWrapper'
import { getWebSocketUrl } from '@/composables/useWebSocketUrl'
import { useI18n } from 'vue-i18n'
import { cloneAssistantData } from '@/components/panels/workflowAssistantActionUtils'

// Node types from kmlib
export type NodeType = 'agent' | 'input' | 'join' | 'pause' | 'base64' | 'excel' | 'ocr' | 'email' | 'knowledge_base' | 'ingestion'

// Edge configuration types from kmlib base_node_config.py
export interface EdgeConfig {
    target: string
    label?: string
    transform?: string
}

export interface ConditionT {
    target: string
    expression: string
}

export interface ParallelT {
    targets: string[]
    iterator: 'broadcast' | 'for_each'
    downstream_join_id?: string
    pre_map_label?: string
    post_map_label?: string
    edge_label?: string
    transform?: string
}

export type NextNode = string | EdgeConfig | ConditionT[] | ParallelT

// Base node interface matching base_node_config.py
export interface BaseNodeConfig {
    id: string
    node_type: NodeType
    name: string
    next_node?: NextNode
    parameters?: Record<string, any>
}

export type InputNodeReturnType = 'auto' | 'string' | 'object' | 'array' | 'integer' | 'number' | 'boolean'

// Agent node matching agent_node_config.py
export interface AgentNodeConfig extends BaseNodeConfig {
    node_type: 'agent'
    role?: string
    llm_config: string | Record<string, any>
    question?: string
    description?: string
    memory?: boolean
    return_complete_object?: boolean
    return_config?: {
        type?: string
        model_name?: string
        json_schema?: Record<string, any>
    }
    output_config?: {
        type?: string
        model_name?: string
        json_schema?: Record<string, any>
    }
    vector_store_config?: Record<string, any>
    knowledge_base_indexes?: string[]
}

// Input node matching input_node_config.py
export interface InputNodeConfig extends BaseNodeConfig {
    node_type: 'input'
    content?: string | Record<string, any> | Array<unknown> | number | boolean
    return_type?: InputNodeReturnType
}

export interface PauseNodeConfig extends BaseNodeConfig {
    node_type: 'pause'
    duration_seconds?: string | number
    description?: string
}

// Join node matching join_node_config.py
export interface JoinNodeConfig extends BaseNodeConfig {
    node_type: 'join'
    parameters?: {
        reducer?: string
        initial_factory?: string
    }
}


// Base64 file output node
export interface Base64NodeConfig extends BaseNodeConfig {
    node_type: 'base64'
    file_type?: 'docx' | 'pdf' | 'txt' | 'json'
    content?: string
    css?: string
}

// Excel cell style configuration - must match backend ExcelCellStyleConfig
export interface ExcelCellStyleConfig {
    bold?: boolean
    bg_color?: string
    font_color?: string
    font_size?: number
    alignment?: 'left' | 'center' | 'right'
    border?: boolean
}

// Excel sheet configuration - must match backend ExcelSheetConfig
export interface ExcelSheetConfig {
    name: string
    headers?: string[]
    data: any  // Can be string (state reference like "{{state.items}}") or array
    column_widths?: number[]
    freeze_header?: boolean
    header_style?: ExcelCellStyleConfig
}

// Excel node configuration
export interface ExcelNodeConfig extends BaseNodeConfig {
    node_type: 'excel'
    sheets: ExcelSheetConfig[]
}

// OCR document analysis config
export interface OcrDocumentConfig {
    cloud_provider?: string
    doc_analysis_provider?: string
    params?: {
        api_url?: string
        api_key?: string
        llm_config?: Record<string, any>
        min_text_length_per_page?: number
        max_concurrent_chunks?: number
        max_pages_per_call?: number
        ocr_full_doc_threshold?: number
    }
}

// OCR node configuration
export interface OcrNodeConfig extends BaseNodeConfig {
    node_type: 'ocr'
    description?: string
    file_url?: string
    file_path?: string
    file_base64?: string
    file_type: 'pdf' | 'image'
    pdf_mode?: 'extract' | 'ocr'
    output_format?: 'text' | 'markdown'
    include_metadata?: boolean
    compact_metadata?: boolean
    metadata_fields?: ('tables' | 'sections' | 'styles' | 'figures')[]
    image_media_type?: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/bmp' | 'image/tiff' | 'image/webp'
    config: OcrDocumentConfig
}

// Email recipient configuration
export interface EmailRecipient {
    address: string
    displayName?: string
}

// Email attachment configuration
export interface EmailAttachment {
    name: string
    contentType: string
    contentInBase64: string
    contentId?: string
}

// Email node configuration
export interface EmailNodeConfig extends BaseNodeConfig {
    node_type: 'email'
    subject: string
    html_body?: string
    plain_text_body?: string
    to: EmailRecipient[]
    cc?: EmailRecipient[]
    bcc?: EmailRecipient[]
    attachments?: EmailAttachment[]
    wait_for_completion?: boolean
    timeout_seconds?: number
}

// Knowledge base search node configuration
export interface KnowledgeBaseNodeConfig extends BaseNodeConfig {
    node_type: 'knowledge_base'
    description?: string
    query: string
    index_name: string
    embedding_model_id: number
    vectorstore_config_id: number
    top_k: number
    search_type: 'hybrid' | 'semantic_hybrid' | 'similarity'
    score_threshold?: number | null
    vector_store_config?: Record<string, any>
}

// Ingestion node configuration matching ingestion_node_config.py
export interface IngestionNodeConfig extends BaseNodeConfig {
    node_type: 'ingestion'
    description?: string
    content: string | Array<string | Record<string, string>>
    index_name: string
    chunk_size?: number
    chunk_overlap?: number
    document_id?: string
    filename?: string
    knowledge_base_id?: string
    metadata?: Record<string, any>
    vector_store_config?: Record<string, any>
}

export type WorkflowNode = AgentNodeConfig | InputNodeConfig | JoinNodeConfig | PauseNodeConfig | Base64NodeConfig | ExcelNodeConfig | OcrNodeConfig | EmailNodeConfig | KnowledgeBaseNodeConfig | IngestionNodeConfig

// Workflow service matching workflow_services_model.py
export interface WorkflowService {
    id?: number
    workflow_id?: number
    name: string
    service_config: {
        provider_config: {
            cloud_provider: string
            model_provider: string
            prompt: string
            params: Record<string, any>
        }
        available_tools?: string[]
    }
}

// Workflow state matching workflow_state_model.py
export interface WorkflowState {
    id?: number
    workflow_id?: number
    model_name: string
    type: string
    json_schema: Record<string, any>
    node_output?: boolean
}

// Node from database matching node_model.py
export interface NodeRecord {
    id?: number
    workflow_id: number
    order: number
    node_config: WorkflowNode
}

// Complete workflow definition matching PublicWorkflowConfig
export interface WorkflowDefinition {
    id?: number
    name: string
    description?: string
    create_linked_bucket?: boolean
    start_node: string
    nodes: WorkflowNode[]
    services?: Record<string, WorkflowService['service_config']>
    state?: WorkflowState
    output_configs?: Record<string, any>
    // Skill fields (Phase 4a — agentic_workflows)
    skill_enabled?: boolean
    skill_description?: string
    input_schema?: Record<string, any> | null
    output_description?: string
}

export interface WorkflowEvent {
    event_id: string
    task_id: string
    event_type: string
    message: string
    data?: Record<string, any>
    timestamp: number
    sequence: number
}

export type NodeStatus = 'pending' | 'running' | 'completed' | 'error'

export interface WorkflowCosts {
    run_id?: string
    total_cost_eur: number
    summary?: {
        total_cost_usd: number
        entries: number
        by_service?: Record<string, { cost: number; entries: number; models: string[] }>
        by_provider?: Record<string, { cost: number; entries: number; models: string[] }>
        by_model?: Record<string, { cost: number; entries: number; tokens?: number }>
        period?: {
            start?: string
            end?: string
        }
    }
}

export interface WorkflowRun {
    taskId: string
    workflowId: number
    workflowName: string
    status: 'pending' | 'running' | 'completed' | 'error' | 'cancelled'
    startedAt: Date
    completedAt?: Date
    events: WorkflowEvent[]
    nodeStatuses: Map<string, NodeStatus>
    result?: any
    hasDownload?: boolean
    error?: string
    costs?: WorkflowCosts
}

export interface WorkflowExecution {
    id: number
    workflow_id: number
    run_id: string
    status: 'pending' | 'running' | 'success' | 'error' | 'cancelled'
    started_at: string
    completed_at?: string | null
    duration_seconds?: number | null
    total_cost_eur: number
    cost_summary?: string | null
    result_summary?: string | null
    error_message?: string | null
    node_count: number
}

export interface WorkflowExecutionHistoryQuery {
    startedFrom?: string | null
    startedTo?: string | null
    limit?: number | null
    offset?: number
}

export interface NodeSnapshot {
    node_id: string
    snapshot_id: string
    status: 'created' | 'pending' | 'running' | 'success' | 'error'
    timestamp: string
    duration?: number | null
    error_message?: string | null
    data?: any
    execution_path: string[]
}

// Workflow approval types
export type ApprovalStatusType = 'draft' | 'pending' | 'approved' | 'rejected' | 'expired' | 'revoked'

export interface ApprovalStatus {
    workflow_id: number
    status: ApprovalStatusType | null
    approved_until: string | null
}

export interface ApprovalAttachment {
    name: string
    data: string
    mime_type: string
}

export interface WorkflowApproval {
    id: number
    approval_type: string
    workflow_id?: number | null
    workflow_name?: string | null
    agent_id?: number | null
    agent_name?: string | null
    app_id: number
    status: ApprovalStatusType
    title: string
    description?: string | null
    known_risks?: string | null
    compensating_controls?: string | null
    security_standards?: string | null
    validity_days: number
    attachments?: { name: string; mime_type: string }[] | null
    risk_analysis?: string | null
    requested_by?: string | null
    requested_at?: string | null
    reviewed_by?: string | null
    reviewed_at?: string | null
    reviewer_notes?: string | null
    servicenow_link?: string | null
    approved_until?: string | null
    created_at: string
    updated_at: string
}

export interface SubmitApprovalPayload {
    title: string
    description: string
    known_risks?: string
    compensating_controls?: string
    security_standards?: string
    validity_days: number
    attachments?: ApprovalAttachment[]
}

export interface ReviewApprovalPayload {
    decision: 'approved' | 'rejected'
    reviewer_notes?: string
    servicenow_link?: string
}

export interface ApprovalConfig {
    notification_email: string | null
    risk_agent_id: number | null
    risk_agent_name: string | null
    workflow_assistant_model: string | null
}

export interface UpdateApprovalConfigPayload {
    notification_email: string | null
    risk_agent_id: number | null
    workflow_assistant_model: string | null
}

export type WorkflowAssistantActionType = 'add_node' | 'update_node' | 'remove_node' | 'connect_nodes' | 'disconnect_nodes'

export interface WorkflowAssistantAction {
    action_type: WorkflowAssistantActionType
    reason?: string
    node_id?: string | null
    node_type?: string | null
    node_name?: string | null
    position?: { x: number; y: number } | null
    source_id?: string | null
    target_id?: string | null
    updates?: Record<string, unknown> | null
}

export interface WorkflowAssistantActionSet {
    messageId: string
    actions: WorkflowAssistantAction[]
    generatedAt: number
}

export const useWorkflowStore = defineStore('workflowStore', () => {
    const { guid, axiosBearerHeader, rootGuid, rootAxiosBearerHeader, portalBaseUrl } = useApiCredentials()
    const { handleAxiosError, rootAuth } = useWrapper()
    const { t } = useI18n()

    // State
    const workflows: Ref<WorkflowDefinition[]> = ref([])
    const currentWorkflowId: Ref<number | null> = ref(null)
    const currentRun: Ref<WorkflowRun | null> = ref(null)
    const isConnected: Ref<boolean> = ref(false)
    const events: Ref<WorkflowEvent[]> = ref([])
    const websocket: Ref<WebSocket | null> = ref(null)
    const connectionError: Ref<string | null> = ref(null)
    const isLoading: Ref<boolean> = ref(false)
    const isSaving: Ref<boolean> = ref(false)
    const availableModels: Ref<string[]> = ref([])
    const availableModelsInfo: Ref<{ name: string; config_type?: string; cloud_provider?: string | null; model_provider?: string | null; supported_tools?: string[] | null; supported_mcp_servers?: string[] | null; supports_responses?: boolean | null; supports_vision?: boolean | null }[]> = ref([])
    const assistantPreview: Ref<WorkflowAssistantActionSet | null> = ref(null)
    const assistantPreviewNodePositions: Ref<Record<string, { x: number; y: number }>> = ref({})
    const assistantRecentChanges: Ref<WorkflowAssistantActionSet | null> = ref(null)
    const assistantPendingChanges: Ref<WorkflowAssistantActionSet | null> = ref(null)
    const assistantFocusedMessageId: Ref<string | null> = ref(null)
    const pendingCanvasNodePositions: Ref<Record<string, { x: number; y: number }>> = ref({})
    let assistantRecentChangesTimer: ReturnType<typeof setTimeout> | null = null

    // Approval state
    const approvalStatuses: Ref<ApprovalStatus[]> = ref([])
    const currentApproval: Ref<WorkflowApproval | null> = ref(null)
    const allApprovals: Ref<WorkflowApproval[]> = ref([])
    const isLoadingApprovals: Ref<boolean> = ref(false)
    const approvalConfig: Ref<ApprovalConfig | null> = ref(null)
    const isLoadingApprovalConfig: Ref<boolean> = ref(false)

    // Execution history
    const executionHistory: Ref<WorkflowExecution[]> = ref([])
    const isLoadingHistory: Ref<boolean> = ref(false)
    const executionHistoryQuery: Ref<WorkflowExecutionHistoryQuery> = ref({})
    const executionSnapshots: Ref<NodeSnapshot[]> = ref([])
    const isLoadingSnapshots: Ref<boolean> = ref(false)

    // Editor state
    const selectedNodeId: Ref<string | null> = ref(null)
    const editMode: Ref<boolean> = ref(false)

    // Reactivity trigger for Map changes (Vue doesn't track Map mutations automatically)
    const nodeStatusVersion: Ref<number> = ref(0)

    // Computed
    const nodeStatuses = computed(() => {
        // Access version to create dependency
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = nodeStatusVersion.value
        if (!currentRun.value) return new Map<string, NodeStatus>()
        return currentRun.value.nodeStatuses
    })

    const currentWorkflow = computed(() => {
        if (!currentWorkflowId.value) return null
        return workflows.value.find(w => w.id === currentWorkflowId.value)
    })

    const selectedNode = computed(() => {
        if (!selectedNodeId.value || !currentWorkflow.value) return null
        return currentWorkflow.value.nodes.find(n => n.id === selectedNodeId.value)
    })

    const portalUrl = computed(() => portalBaseUrl)

    const normalizeWorkflowNodes = (nodes: WorkflowNode[]): WorkflowNode[] => {
        const nodeTypeById = new Map(nodes.map(node => [node.id, node.node_type]))

        return nodes.map(node => {
            if (typeof node.next_node === 'string') {
                if (nodeTypeById.get(node.next_node) === 'join') {
                    return node
                }
                return {
                    ...node,
                    next_node: { target: node.next_node },
                } as WorkflowNode
            }

            if (
                node.next_node
                && typeof node.next_node === 'object'
                && !Array.isArray(node.next_node)
                && 'target' in node.next_node
                && !('targets' in node.next_node)
                && nodeTypeById.get(node.next_node.target) === 'join'
            ) {
                return {
                    ...node,
                    next_node: node.next_node.target,
                } as WorkflowNode
            }

            return node
        })
    }

    const normalizeWorkflowDefinition = (workflow: WorkflowDefinition): WorkflowDefinition => ({
        ...workflow,
        nodes: normalizeWorkflowNodes(workflow.nodes),
    })

    const normalizeJoinConnectionsForPersist = (nodes: WorkflowNode[]): WorkflowNode[] => {
        const nodeTypeById = new Map(nodes.map(node => [node.id, node.node_type]))

        return nodes.map(node => {
            if (!node.next_node) {
                return node
            }

            if (typeof node.next_node !== 'object' || Array.isArray(node.next_node) || !('target' in node.next_node) || 'targets' in node.next_node) {
                return node
            }

            const edgeConfig = node.next_node
            if (nodeTypeById.get(edgeConfig.target) !== 'join') {
                return node
            }

            return {
                ...node,
                next_node: edgeConfig.target,
            } as WorkflowNode
        })
    }

    const normalizeWorkflowForPersist = <T extends { nodes?: WorkflowNode[] }>(workflow: T): T => {
        if (!workflow.nodes) {
            return workflow
        }
        return {
            ...workflow,
            nodes: normalizeJoinConnectionsForPersist(workflow.nodes),
        }
    }

    // ==================== WORKFLOW CRUD ====================

    const loadWorkflows = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            isLoading.value = true
            try {
                const { data } = await axios.get<WorkflowDefinition[]>(
                    `${portalUrl.value}/${guid.value}/my-workflows`,
                    axiosBearerHeader.value
                )
                workflows.value = data.map(normalizeWorkflowDefinition)
            } finally {
                isLoading.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.load.error' }
    )

    /**
     * Resolve which application owns a given workflow by ID.
     * Returns the app_guid or null when the workflow is not found.
     */
    const resolveWorkflowApp = rootAuth(async (workflowId: number): Promise<string | null> => {
        try {
            const { data } = await axios.get<{ app_guid: string }>(
                `${portalUrl.value}/${guid.value}/workflows/${workflowId}/resolve-app`,
                axiosBearerHeader.value
            )
            return data.app_guid
        } catch {
            return null
        }
    }, false)

    const loadWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number): Promise<WorkflowDefinition> => {
            isLoading.value = true
            try {
                const { data } = await axios.get<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}`,
                    axiosBearerHeader.value
                )
                // Update in local array
                const idx = workflows.value.findIndex(w => w.id === workflowId)
                const normalized = normalizeWorkflowDefinition(data)
                if (idx >= 0) {
                    workflows.value[idx] = normalized
                } else {
                    workflows.value.push(normalized)
                }
                currentWorkflowId.value = workflowId
                return normalized
            } finally {
                isLoading.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.load.error' }
    )

    const createWorkflow = handleAxiosError(
        rootAuth(async (workflow: Omit<WorkflowDefinition, 'id'>): Promise<WorkflowDefinition> => {
            isSaving.value = true
            try {
                const payload = normalizeWorkflowForPersist(workflow)
                const { data } = await axios.post<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/workflow`,
                    payload,
                    axiosBearerHeader.value
                )
                const normalized = normalizeWorkflowDefinition(data)
                workflows.value.push(normalized)
                currentWorkflowId.value = normalized.id!
                return normalized
            } finally {
                isSaving.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.create.error', successMsg: 'stores.workflow.create.success' }
    )

    const updateWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number, workflow: Partial<WorkflowDefinition>): Promise<WorkflowDefinition> => {
            isSaving.value = true
            try {
                const payload = normalizeWorkflowForPersist(workflow)
                const { data } = await axios.put<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}`,
                    payload,
                    axiosBearerHeader.value
                )
                const idx = workflows.value.findIndex(w => w.id === workflowId)
                const normalized = normalizeWorkflowDefinition(data)
                if (idx >= 0) {
                    workflows.value[idx] = normalized
                }
                return normalized
            } finally {
                isSaving.value = false
            }
        }, false),
        {
            errorMsg: 'stores.workflow.update.error',
            successMsg: 'stores.workflow.update.success',
            getErrorMsg: (error: any) => {
                const status = error?.response?.status
                if (status === 403) return 'stores.workflow.update.forbidden'
                if (status === 423) return 'stores.workflow.update.locked'
                if (status === 409) return 'stores.workflow.update.conflict'
                return 'stores.workflow.update.error'
            },
        }
    )

    const updateWorkflowSkill = handleAxiosError(
        rootAuth(async (workflowId: number, skill: {
            skill_enabled: boolean
            skill_description?: string
            input_schema?: Record<string, any> | null
            output_description?: string
        }): Promise<WorkflowDefinition> => {
            isSaving.value = true
            try {
                const { data } = await axios.patch<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/skill`,
                    skill,
                    axiosBearerHeader.value
                )
                const idx = workflows.value.findIndex(w => w.id === workflowId)
                const normalized = normalizeWorkflowDefinition(data)
                if (idx >= 0) {
                    workflows.value[idx] = normalized
                }
                return normalized
            } finally {
                isSaving.value = false
            }
        }, false),
        {
            errorMsg: 'stores.workflow.update.error',
            successMsg: 'stores.workflow.update.success',
            getErrorMsg: (error: any) => {
                const status = error?.response?.status
                if (status === 403) return 'stores.workflow.update.forbidden'
                if (status === 423) return 'stores.workflow.update.locked'
                if (status === 409) return 'stores.workflow.update.conflict'
                return 'stores.workflow.update.error'
            },
        }
    )

    const deleteWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number): Promise<void> => {
            isSaving.value = true
            try {
                await axios.delete(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}`,
                    axiosBearerHeader.value
                )
                workflows.value = workflows.value.filter(w => w.id !== workflowId)
                if (currentWorkflowId.value === workflowId) {
                    currentWorkflowId.value = null
                }
            } finally {
                isSaving.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.delete.error', successMsg: 'stores.workflow.delete.success' }
    )

    const copyWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number): Promise<WorkflowDefinition> => {
            isSaving.value = true
            try {
                const { data } = await axios.post<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/copy`,
                    {},
                    axiosBearerHeader.value
                )
                const normalized = normalizeWorkflowDefinition(data)
                workflows.value.push(normalized)
                currentWorkflowId.value = normalized.id!
                return normalized
            } finally {
                isSaving.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.copy.error', successMsg: 'stores.workflow.copy.success' }
    )

    const publishWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number, targetAppGuid: string): Promise<WorkflowDefinition> => {
            isSaving.value = true
            try {
                const { data } = await axios.post<WorkflowDefinition>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/publish`,
                    { target_app_guid: targetAppGuid },
                    axiosBearerHeader.value
                )
                return data
            } finally {
                isSaving.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.publish.error', successMsg: 'stores.workflow.publish.success' }
    )

    // ==================== AVAILABLE MODELS ====================

    const loadAvailableModels = handleAxiosError(
        rootAuth(async (): Promise<string[]> => {
            try {
                const { data } = await axios.get<{ name: string; config_type?: string; cloud_provider?: string | null; model_provider?: string | null; supported_tools?: string[] | null; supported_mcp_servers?: string[] | null; supports_responses?: boolean | null; supports_vision?: boolean | null }[]>(
                    `${portalUrl.value}/${guid.value}/available/models`,
                    axiosBearerHeader.value
                )
                availableModelsInfo.value = data
                availableModels.value = data.filter(m => !m.config_type || m.config_type === 'llm').map(m => m.name)
                return availableModels.value
            } catch (e) {
                console.error('Failed to load available models:', e)
                return []
            }
        }, false),
        { errorMsg: 'stores.workflow.models.error' }
    )

    // ==================== NODE CRUD ====================

    const addNode = (node: WorkflowNode, autoSelect: boolean = true) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        // Create a deep copy to avoid reactivity issues
        const nodeCopy = cloneAssistantData(node)

        // Find and update the workflow in the array properly to trigger reactivity
        const idx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (idx >= 0) {
            // Create a new workflow object with the new node added
            const updatedWorkflow = {
                ...workflows.value[idx],
                nodes: [...workflows.value[idx].nodes, nodeCopy]
            }
            workflows.value[idx] = updatedWorkflow
        }

        if (autoSelect) {
            selectedNodeId.value = node.id
        }
    }

    const replaceCurrentWorkflow = (workflow: WorkflowDefinition) => {
        const normalized = normalizeWorkflowDefinition(cloneAssistantData(workflow))
        const workflowId = normalized.id ?? currentWorkflowId.value
        if (!workflowId) return

        const workflowIdx = workflows.value.findIndex(w => w.id === workflowId)
        if (workflowIdx >= 0) {
            workflows.value[workflowIdx] = normalized
        } else {
            workflows.value.push(normalized)
        }

        currentWorkflowId.value = workflowId
    }

    const updateNode = (nodeId: string, updates: Partial<WorkflowNode>) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        const workflowIdx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (workflowIdx < 0) return

        const nodeIdx = workflows.value[workflowIdx].nodes.findIndex(n => n.id === nodeId)
        if (nodeIdx >= 0) {
            // Create new node object with updates
            const updatedNode = { ...workflows.value[workflowIdx].nodes[nodeIdx], ...updates } as WorkflowNode
            // Create new nodes array
            const newNodes = [...workflows.value[workflowIdx].nodes]
            newNodes[nodeIdx] = updatedNode
            // Update workflow with new nodes array
            workflows.value[workflowIdx] = {
                ...workflows.value[workflowIdx],
                nodes: newNodes
            }
        }
    }

    const setStartNode = (nodeId: string) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        const workflowIdx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (workflowIdx < 0) return

        const workflow = workflows.value[workflowIdx]
        const nodeExists = workflow.nodes.some(n => n.id === nodeId)
        if (!nodeExists) return

        const updatedNodes = workflow.nodes.map(node => {
            // Keep the new start node untouched and skip nodes without outgoing edges
            if (node.id === nodeId || !node.next_node) return node

            // Simple string edge
            if (typeof node.next_node === 'string') {
                if (node.next_node === nodeId) {
                    return { ...node, next_node: undefined }
                }
                return node
            }

            // EdgeConfig: { target, label?, transform? }
            if (typeof node.next_node === 'object' && 'target' in node.next_node && !('targets' in node.next_node)) {
                if ((node.next_node as EdgeConfig).target === nodeId) {
                    return { ...node, next_node: undefined }
                }
                return node
            }

            // ParallelT: remove new start node from targets
            if (typeof node.next_node === 'object' && 'targets' in node.next_node) {
                const parallel = node.next_node as ParallelT
                const filtered = parallel.targets.filter(t => t !== nodeId)
                if (filtered.length === 0) {
                    return { ...node, next_node: undefined }
                }
                if (filtered.length !== parallel.targets.length) {
                    return { ...node, next_node: { ...parallel, targets: filtered } }
                }
                return node
            }

            // ConditionT[]
            if (Array.isArray(node.next_node)) {
                const filtered = (node.next_node as ConditionT[]).filter(c => c.target !== nodeId)
                if (filtered.length === 0) {
                    return { ...node, next_node: undefined }
                }
                if (filtered.length !== (node.next_node as ConditionT[]).length) {
                    return { ...node, next_node: filtered }
                }
                return node
            }

            return node
        })

        workflows.value[workflowIdx] = {
            ...workflow,
            start_node: nodeId,
            nodes: updatedNodes,
        }
    }

    const removeNode = (nodeId: string) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        const workflowIdx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (workflowIdx < 0) return

        // Filter out the node and clean up all edge types pointing to it
        const newNodes = workflows.value[workflowIdx].nodes
            .filter(n => n.id !== nodeId)
            .map(node => {
                if (!node.next_node) return node

                // Simple string edge
                if (typeof node.next_node === 'string') {
                    if (node.next_node === nodeId) {
                        return { ...node, next_node: undefined }
                    }
                    return node
                }

                // EdgeConfig: { target, label?, transform? }
                if (typeof node.next_node === 'object' && 'target' in node.next_node && !('targets' in node.next_node)) {
                    if ((node.next_node as EdgeConfig).target === nodeId) {
                        return { ...node, next_node: undefined }
                    }
                    return node
                }

                // ParallelT: { targets: [...], iterator, ... }
                if (typeof node.next_node === 'object' && 'targets' in node.next_node) {
                    const parallel = node.next_node as ParallelT
                    const filtered = parallel.targets.filter(t => t !== nodeId)
                    if (filtered.length === 0) {
                        return { ...node, next_node: undefined }
                    }
                    // Also clear downstream_join_id if it points to the deleted node
                    const updatedParallel = { ...parallel, targets: filtered }
                    if (updatedParallel.downstream_join_id === nodeId) {
                        updatedParallel.downstream_join_id = undefined
                    }
                    if (filtered.length !== parallel.targets.length || parallel.downstream_join_id === nodeId) {
                        return { ...node, next_node: updatedParallel }
                    }
                    return node
                }

                // ConditionT[]: [{ target, expression }, ...]
                if (Array.isArray(node.next_node)) {
                    const filtered = (node.next_node as ConditionT[]).filter(c => c.target !== nodeId)
                    if (filtered.length === 0) {
                        return { ...node, next_node: undefined }
                    }
                    if (filtered.length !== (node.next_node as ConditionT[]).length) {
                        return { ...node, next_node: filtered }
                    }
                    return node
                }

                return node
            })

        workflows.value[workflowIdx] = {
            ...workflows.value[workflowIdx],
            nodes: newNodes
        }

        if (selectedNodeId.value === nodeId) {
            selectedNodeId.value = null
        }
    }

    const connectNodes = (sourceId: string, targetId: string, config?: Partial<EdgeConfig | ParallelT>) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        const workflowIdx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (workflowIdx < 0) return

        const nodeIdx = workflows.value[workflowIdx].nodes.findIndex(n => n.id === sourceId)
        if (nodeIdx < 0) return

        const targetNode = workflows.value[workflowIdx].nodes.find(n => n.id === targetId)

        let newNextNode: NextNode
        if (targetNode?.node_type === 'join' && !(config && 'targets' in config)) {
            newNextNode = targetId
        } else if (config && 'targets' in config) {
            // ParallelT connection
            newNextNode = { targets: [targetId], iterator: 'for_each', ...config } as ParallelT
        } else if (config) {
            // EdgeConfig connection
            newNextNode = { target: targetId, ...config } as EdgeConfig
        } else {
            // Normalize simple connections to EdgeConfig.
            newNextNode = { target: targetId }
        }

        // Create new node with updated next_node
        const updatedNode = { ...workflows.value[workflowIdx].nodes[nodeIdx], next_node: newNextNode }
        const newNodes = [...workflows.value[workflowIdx].nodes]
        newNodes[nodeIdx] = updatedNode

        workflows.value[workflowIdx] = {
            ...workflows.value[workflowIdx],
            nodes: newNodes
        }
    }

    const disconnectNodes = (sourceId: string, targetId?: string) => {
        if (!currentWorkflow.value || !currentWorkflowId.value) return

        const workflowIdx = workflows.value.findIndex(w => w.id === currentWorkflowId.value)
        if (workflowIdx < 0) return

        const nodeIdx = workflows.value[workflowIdx].nodes.findIndex(n => n.id === sourceId)
        if (nodeIdx < 0) return

        const sourceNode = workflows.value[workflowIdx].nodes[nodeIdx]
        let newNextNode: NextNode | undefined = undefined

        if (!targetId) {
            newNextNode = undefined
        } else if (typeof sourceNode.next_node === 'object' && 'targets' in sourceNode.next_node) {
            const parallel = sourceNode.next_node as ParallelT
            const newTargets = parallel.targets.filter(t => t !== targetId)
            if (newTargets.length > 0) {
                newNextNode = { ...parallel, targets: newTargets }
            }
        } else if (Array.isArray(sourceNode.next_node)) {
            // ConditionT[]: remove condition entries targeting this node
            const remaining = (sourceNode.next_node as ConditionT[]).filter(c => c.target !== targetId)
            newNextNode = remaining.length > 0 ? remaining : undefined
        } else if (typeof sourceNode.next_node === 'string') {
            // Simple string target
            newNextNode = sourceNode.next_node === targetId ? undefined : sourceNode.next_node
        } else if (typeof sourceNode.next_node === 'object' && 'target' in sourceNode.next_node) {
            // EdgeConfig
            const edge = sourceNode.next_node as EdgeConfig
            newNextNode = edge.target === targetId ? undefined : sourceNode.next_node
        } else {
            newNextNode = sourceNode.next_node
        }

        // Create new node with updated next_node
        const updatedNode = { ...sourceNode, next_node: newNextNode }
        const newNodes = [...workflows.value[workflowIdx].nodes]
        newNodes[nodeIdx] = updatedNode

        workflows.value[workflowIdx] = {
            ...workflows.value[workflowIdx],
            nodes: newNodes
        }
    }

    const setAssistantPreview = (messageId: string, actions: WorkflowAssistantAction[]) => {
        assistantPreview.value = {
            messageId,
            actions: cloneAssistantData(actions),
            generatedAt: Date.now(),
        }
        assistantPreviewNodePositions.value = {}
    }

    const clearAssistantPreview = (messageId?: string) => {
        if (!messageId || assistantPreview.value?.messageId === messageId) {
            assistantPreview.value = null
            assistantPreviewNodePositions.value = {}
        }
    }

    const setAssistantPreviewNodePosition = (nodeId: string, position: { x: number; y: number }) => {
        assistantPreviewNodePositions.value = {
            ...assistantPreviewNodePositions.value,
            [nodeId]: { ...position },
        }
    }

    const queuePendingCanvasNodePosition = (nodeId: string, position: { x: number; y: number }) => {
        pendingCanvasNodePositions.value = {
            ...pendingCanvasNodePositions.value,
            [nodeId]: { ...position },
        }
    }

    const takePendingCanvasNodePositions = (): Record<string, { x: number; y: number }> => {
        const positions = { ...pendingCanvasNodePositions.value }
        pendingCanvasNodePositions.value = {}
        return positions
    }

    const flashAssistantRecentChanges = (messageId: string, actions: WorkflowAssistantAction[], durationMs: number = 4800) => {
        const existing = assistantRecentChanges.value
        const merged = existing?.messageId === messageId
            ? [...existing.actions, ...cloneAssistantData(actions)]
            : cloneAssistantData(actions)

        assistantRecentChanges.value = {
            messageId,
            actions: merged,
            generatedAt: Date.now(),
        }

        if (assistantRecentChangesTimer) {
            clearTimeout(assistantRecentChangesTimer)
        }

        assistantRecentChangesTimer = setTimeout(() => {
            assistantRecentChanges.value = null
            assistantRecentChangesTimer = null
        }, durationMs)
    }

    const clearAssistantRecentChanges = () => {
        if (assistantRecentChangesTimer) {
            clearTimeout(assistantRecentChangesTimer)
            assistantRecentChangesTimer = null
        }
        assistantRecentChanges.value = null
    }

    const setAssistantPendingChanges = (messageId: string, actions: WorkflowAssistantAction[]) => {
        const existing = assistantPendingChanges.value
        const merged = existing?.messageId === messageId
            ? [...existing.actions, ...cloneAssistantData(actions)]
            : cloneAssistantData(actions)
        assistantPendingChanges.value = {
            messageId,
            actions: merged,
            generatedAt: Date.now(),
        }
    }

    const clearAssistantPendingChanges = (messageId?: string) => {
        if (!messageId || assistantPendingChanges.value?.messageId === messageId) {
            assistantPendingChanges.value = null
        }
    }

    const setAssistantFocusedMessage = (messageId: string | null) => {
        assistantFocusedMessageId.value = messageId
    }

    // ==================== WORKFLOW EXECUTION ====================

    // Media input interface for workflow execution
    interface MediaInputPayload {
        data: string
        media_type: string
        filename?: string
        key?: string
    }

    interface ArtifactRefPayload {
        key: string
        artifact_ref: string
    }

    const startWorkflow = handleAxiosError(
        rootAuth(async (
            workflowId: number,
            inputData?: any,
            mediaInputs?: MediaInputPayload[],
            artifactRefs?: ArtifactRefPayload[],
        ): Promise<string> => {
            const workflow = workflows.value.find(w => w.id === workflowId)

            // Build request payload – only send a body when there is actual input
            const hasInput = inputData !== undefined && inputData !== null
            const hasMedia = mediaInputs && mediaInputs.length > 0
            const hasArtifactRefs = artifactRefs && artifactRefs.length > 0

            const { data } = await axios.post<string>(
                `${portalBaseUrl}/${rootGuid.value}/graph/${workflowId}/preview`,
                hasInput || hasMedia || hasArtifactRefs
                    ? {
                        ...(hasInput ? { input_data: inputData } : {}),
                        ...(hasMedia ? { media_inputs: mediaInputs } : {}),
                        ...(hasArtifactRefs ? { artifact_refs: artifactRefs } : {}),
                    }
                    : undefined,
                {
                    ...rootAxiosBearerHeader.value,
                    headers: {
                        ...rootAxiosBearerHeader.value.headers,
                        'Content-Type': 'application/json'
                    }
                }
            )

            const taskId = data.toString().trim().replace(/"/g, '')

            // Initialize current run with node statuses
            const nodeStatusMap = new Map<string, NodeStatus>()
            if (workflow) {
                workflow.nodes.forEach(node => {
                    nodeStatusMap.set(node.id, 'pending')
                })
            }

            currentRun.value = {
                taskId,
                workflowId,
                workflowName: workflow?.name || `Workflow ${workflowId}`,
                status: 'pending',
                startedAt: new Date(),
                events: [],
                nodeStatuses: nodeStatusMap
            }

            // Reset execution counts and event data for new run
            nodeExecutionCounts.value.clear()
            nodeEventData.value.clear()
            nodeRunningIterations.value.clear()

            await connectWebSocket(taskId)
            return taskId
        }, true),
        { errorMsg: 'stores.workflow.start.error' }
    )

    const getWorkflowStatus = handleAxiosError(
        rootAuth(async (taskId: string): Promise<{ state: string; info?: any }> => {
            const { data } = await axios.get<{ state: string; info?: any }>(
                `/${guid.value}/graph/${taskId}`,
                axiosBearerHeader.value
            )

            if (currentRun.value?.taskId === taskId) {
                switch (data.state) {
                    case 'PENDING':
                    case 'STARTED':
                        currentRun.value.status = 'running'
                        break
                    case 'SUCCESS':
                        currentRun.value.status = 'completed'
                        currentRun.value.completedAt = new Date()
                        currentRun.value.result = data.info?.result ?? data.info
                        finalizeWorkflowNodeStates('completed', Date.now() / 1000)
                        // Extract costs from the task result
                        if (data.info?.costs) {
                            currentRun.value.costs = data.info.costs
                        }
                        break
                    case 'FAILURE':
                    case 'REVOKED':
                        currentRun.value.status = data.state === 'REVOKED' ? 'cancelled' : 'error'
                        currentRun.value.completedAt = new Date()
                        currentRun.value.error = data.info?.error || data.info?.message || currentRun.value.error
                        finalizeWorkflowNodeStates(currentRun.value.status, Date.now() / 1000, currentRun.value.error)
                        // Even on failure, we might have partial costs
                        if (data.info?.costs) {
                            currentRun.value.costs = data.info.costs
                        }
                        break
                }
            }

            return data
        }, false),
        { errorMsg: 'stores.workflow.status.error' }
    )

    // ==================== WEBSOCKET ====================

    const connectWebSocket = async (taskId: string): Promise<void> => {
        if (websocket.value) {
            websocket.value.close()
        }

        connectionError.value = null

        const token = axiosBearerHeader.value.headers['Authorization']?.replace('Bearer ', '')
        // Use VITE_WS_URL for WebSocket path prefix (separate from /api/ for HTTP)
        const wsPrefix = import.meta.env.VITE_WS_URL || '/ws'
        const wsUrl = getWebSocketUrl(`${wsPrefix}/?guid=${guid.value}&task_id=${taskId}&token=${token}`)


        try {
            const ws = new WebSocket(wsUrl)
            websocket.value = ws

            ws.onopen = () => {
                isConnected.value = true
                connectionError.value = null
                startHeartbeat()
            }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    handleWebSocketMessage(data)
                } catch (e) {
                    if (event.data === 'heartbeat_ack') return
                    console.error('Failed to parse WebSocket message:', e)
                }
            }

            ws.onerror = (error) => {
                console.error('WebSocket error:', error)
                connectionError.value = t('workflowEditor.connection.error')
            }

            ws.onclose = (event) => {
                isConnected.value = false
                websocket.value = null

                if (event.code !== 1000 && currentRun.value?.status === 'running') {
                    setTimeout(() => {
                        if (currentRun.value?.taskId) {
                            connectWebSocket(currentRun.value.taskId)
                        }
                    }, 3000)
                }
            }
        } catch (error) {
            console.error('Failed to connect WebSocket:', error)
            connectionError.value = t('workflowEditor.connection.failed')
        }
    }

    let heartbeatInterval: ReturnType<typeof setInterval> | null = null

    const startHeartbeat = () => {
        if (heartbeatInterval) clearInterval(heartbeatInterval)
        heartbeatInterval = setInterval(() => {
            if (websocket.value?.readyState === WebSocket.OPEN) {
                websocket.value.send('heartbeat')
            }
        }, 25000)
    }

    // Track execution counts for loop nodes
    const nodeExecutionCounts: Ref<Map<string, number>> = ref(new Map())

    // Track running iterations per node (for parallel execution)
    const nodeRunningIterations: Ref<Map<string, Set<string>>> = ref(new Map())

    // Track node event data (duration, result, status) for display
    interface IterationResult {
        iterationId: string
        result: any
        duration?: number
        completedAt: number
    }

    interface NodeEventData {
        nodeId: string
        startedAt?: number
        completedAt?: number
        duration?: number
        status: NodeStatus
        result?: any
        results?: IterationResult[]  // Array of results for parallel executions
        error?: string
        executionCount: number
        isParallel?: boolean  // Flag to indicate parallel execution
        pendingCompletions?: number  // Track completed iterations waiting for final status
    }
    const nodeEventData: Ref<Map<string, NodeEventData>> = ref(new Map())

    const applyNodeResults = (data: NodeEventData): void => {
        if (data.results?.length && data.results.length > 1) {
            data.result = data.results.map(result => result.result)
            data.isParallel = true
            return
        }

        if (data.results?.length === 1) {
            data.result = data.results[0].result
        }
    }

    const createDefaultNodeEventData = (nodeId: string, status: NodeStatus): NodeEventData => ({
        nodeId,
        status,
        executionCount: nodeExecutionCounts.value.get(nodeId) || 0,
        results: [],
        isParallel: false,
        pendingCompletions: 0,
    })

    const shouldCompleteTerminalNode = (
        nodeId: string,
        data: NodeEventData,
        terminalStatus: WorkflowRun['status'],
    ): boolean => {
        if (terminalStatus === 'completed') {
            return true
        }

        const hasRunningIterations = (nodeRunningIterations.value.get(nodeId)?.size || 0) > 0
        return !hasRunningIterations && (data.pendingCompletions || 0) > 0
    }

    const setFinalNodeState = (
        nodeId: string,
        data: NodeEventData,
        status: Extract<NodeStatus, 'completed' | 'error'>,
        timestamp: number,
        errorMessage?: string,
    ): void => {
        data.status = status
        data.completedAt = timestamp
        data.pendingCompletions = 0
        if (data.startedAt) {
            data.duration = timestamp - data.startedAt
        }
        if (status === 'completed') {
            applyNodeResults(data)
        } else {
            data.error = data.error || errorMessage
        }

        nodeEventData.value.set(nodeId, data)
        currentRun.value?.nodeStatuses.set(nodeId, status)
    }

    const finalizeWorkflowNodeStates = (
        terminalStatus: WorkflowRun['status'],
        timestamp: number,
        errorMessage?: string,
    ): void => {
        if (!currentRun.value) return

        let hasUpdates = false

        for (const [nodeId, currentStatus] of currentRun.value.nodeStatuses.entries()) {
            const existingData = nodeEventData.value.get(nodeId)
            const data: NodeEventData = existingData ? { ...existingData } : createDefaultNodeEventData(nodeId, currentStatus)

            if (currentStatus !== 'running' && data.status !== 'running') {
                continue
            }

            if (shouldCompleteTerminalNode(nodeId, data, terminalStatus)) {
                setFinalNodeState(nodeId, data, 'completed', timestamp)
            } else {
                setFinalNodeState(nodeId, data, 'error', timestamp, errorMessage)
            }

            hasUpdates = true
        }

        if (terminalStatus !== 'running' && terminalStatus !== 'pending') {
            nodeRunningIterations.value.clear()
        }

        if (hasUpdates) {
            nodeStatusVersion.value++
        }
    }

    const handleWebSocketMessage = (data: any) => {

        if (data.event_type === 'heartbeat_ack' || data === 'heartbeat_ack') return
        if (data.event_type === 'connection_established') return
        if (data.event_type === 'ping') return

        const workflowEvent: WorkflowEvent = {
            event_id: data.event_id || crypto.randomUUID(),
            task_id: data.task_id || currentRun.value?.taskId || '',
            event_type: data.event_type || data.type || 'unknown',
            message: data.message || data.content || '',
            data: data.data || data,
            timestamp: data.timestamp || Date.now() / 1000,
            sequence: data.sequence || events.value.length
        }

        events.value.push(workflowEvent)
        if (currentRun.value) {
            currentRun.value.events.push(workflowEvent)
        }

        updateNodeStatusFromEvent(workflowEvent)
        updateWorkflowStatusFromEvent(workflowEvent)
    }

    const updateNodeStatusFromEvent = (event: WorkflowEvent) => {
        if (!currentRun.value) return

        // Extract node ID from message (e.g., "Node research_agent started")
        const messageMatch = event.message.match(/Node (\w+) (started|completed|failed)/)
        const joinMatch = event.message.match(/Join node (\w+) completed/)

        let nodeId: string | null = null
        let action: string | null = null

        if (messageMatch) {
            nodeId = messageMatch[1]
            action = messageMatch[2]
        } else if (joinMatch) {
            nodeId = joinMatch[1]
            action = 'completed'
        }

        // Also check data field for node_id (kmlib sends this)
        if (!nodeId && event.data) {
            nodeId = event.data.node_id || event.data.nodeId || event.data.node?.id
        }

        // Skip workflow-level events
        if (nodeId === 'WORKFLOW') return

        if (!nodeId) return

        const eventType = event.event_type.toLowerCase()
        let status: NodeStatus = 'pending'

        // Get iteration_id from event data (for parallel executions)
        const iterationId = event.data?.iteration_id

        // Get existing node event data or create new
        const existingData = nodeEventData.value.get(nodeId)
        let data: NodeEventData = existingData ? { ...existingData } : {
            nodeId,
            status: 'pending' as NodeStatus,
            executionCount: 0,
            results: [],
            isParallel: false,
            pendingCompletions: 0
        }
        // Ensure results array exists
        if (!data.results) {
            data.results = []
        }

        // When a NEW node starts, mark the previous running node as completed
        // This handles sequential for_each iterations
        if (eventType === 'node_started' || eventType.includes('started') || action === 'started') {
            // Check if another node was running and has pending completions
            for (const [otherNodeId, otherData] of nodeEventData.value.entries()) {
                if (otherNodeId !== nodeId && otherData.status === 'running' &&
                    otherData.pendingCompletions && otherData.pendingCompletions > 0) {
                    // Mark the previous node as completed now that a new node has started
                    const updatedOtherData = { ...otherData }
                    updatedOtherData.status = 'completed'
                    updatedOtherData.completedAt = event.timestamp
                    if (updatedOtherData.startedAt) {
                        updatedOtherData.duration = event.timestamp - updatedOtherData.startedAt
                    }
                    // Set combined result
                    if (updatedOtherData.results && updatedOtherData.results.length > 1) {
                        updatedOtherData.result = updatedOtherData.results.map(r => r.result)
                        updatedOtherData.isParallel = true
                    } else if (updatedOtherData.results && updatedOtherData.results.length === 1) {
                        updatedOtherData.result = updatedOtherData.results[0].result
                    }
                    updatedOtherData.pendingCompletions = 0
                    nodeEventData.value.set(otherNodeId, updatedOtherData)
                    currentRun.value?.nodeStatuses.set(otherNodeId, 'completed')
                    nodeStatusVersion.value++
                }
            }
        }

        // Get or create running iterations set for this node
        if (!nodeRunningIterations.value.has(nodeId)) {
            nodeRunningIterations.value.set(nodeId, new Set())
        }
        const runningIterations = nodeRunningIterations.value.get(nodeId)!

        if (eventType === 'node_started' || eventType.includes('started') || eventType.includes('running') || action === 'started') {
            status = 'running'
            // Track execution count for loop nodes
            const count = nodeExecutionCounts.value.get(nodeId) || 0
            nodeExecutionCounts.value.set(nodeId, count + 1)

            // Track this iteration as running (use iterationId or generate one from count)
            const trackingId = iterationId || `exec_${count + 1}`
            runningIterations.add(trackingId)

            // Only reset results on very first start (no startedAt yet) or new workflow run
            const isFirstStart = !data.startedAt
            const isNewRun = data.status === 'completed' && data.pendingCompletions === 0 && data.results.length === 0

            if (isFirstStart || isNewRun) {
                data.startedAt = event.timestamp
                data.results = []
                data.result = undefined
                data.pendingCompletions = 0
                data.isParallel = false
            }

            // Mark as parallel if multiple iterations are running
            if (runningIterations.size > 1) {
                data.isParallel = true
            }

            data.status = 'running'
            data.executionCount = count + 1
            data.error = undefined
        } else if (eventType === 'node_completed' || eventType.includes('completed') || eventType.includes('success') || action === 'completed') {
            // Use iterationId or find the first running iteration to complete
            const trackingId = iterationId || runningIterations.values().next().value || `exec_${data.executionCount}`

            // Remove this iteration from running set
            runningIterations.delete(trackingId)

            // Add result to the results array
            const iterationResult: IterationResult = {
                iterationId: trackingId,
                result: event.data?.result,
                duration: event.data?.duration,
                completedAt: event.timestamp
            }
            data.results = [...data.results, iterationResult]

            // Track this as a pending completion
            data.pendingCompletions = (data.pendingCompletions || 0) + 1

            // Update the combined result to show all results so far
            if (data.results.length > 1) {
                data.result = data.results.map(r => r.result)
                data.isParallel = true
            } else if (data.results.length === 1) {
                data.result = data.results[0].result
            }

            // Determine status
            if (runningIterations.size === 0) {
                // No more actively running iterations
                // If this is a join node, mark as truly completed
                if (joinMatch) {
                    status = 'completed'
                    data.status = 'completed'
                    data.completedAt = event.timestamp
                    data.pendingCompletions = 0
                    if (data.startedAt) {
                        data.duration = event.timestamp - data.startedAt
                    }
                    applyNodeResults(data)
                } else {
                    // Keep running until next node starts or workflow completes
                    // This handles sequential for_each where we don't know if more iterations are coming
                    status = 'running'
                    data.status = 'running'
                }
            } else {
                // Still running other iterations
                status = 'running'
                data.status = 'running'
            }
        } else if (eventType === 'node_failed' || eventType.includes('error') || eventType.includes('fail') || action === 'failed') {
            status = 'error'

            // Remove this iteration from running set
            const trackingId = iterationId || runningIterations.values().next().value
            if (trackingId) {
                runningIterations.delete(trackingId)
            }

            // Update node event data with error
            data.completedAt = event.timestamp
            data.status = 'error'
            data.pendingCompletions = 0
            if (data.startedAt) {
                data.duration = event.timestamp - data.startedAt
            }
            data.error = event.data?.error || event.message
        }

        // Set the updated data (new object for reactivity)
        nodeEventData.value.set(nodeId, data)
        currentRun.value.nodeStatuses.set(nodeId, status)

        // Trigger Vue reactivity for nodeStatuses computed
        nodeStatusVersion.value++
    }

    const updateWorkflowStatusFromEvent = (event: WorkflowEvent) => {
        if (!currentRun.value) return
        const eventType = event.event_type.toLowerCase()

        // Handle kmlib WORKFLOW_* events
        if (eventType === 'workflow_started' || eventType.includes('workflow_start')) {
            currentRun.value.status = 'running'
        } else if (eventType === 'workflow_completed' || eventType.includes('workflow_complete') || eventType.includes('workflow_success')) {
            currentRun.value.status = 'completed'
            currentRun.value.completedAt = new Date()
            finalizeWorkflowNodeStates('completed', event.timestamp)

            // Result can be in event.data directly (from kmlib) or in event.data.result
            if (event.data) {
                // kmlib sends the result directly in data field
                currentRun.value.result = event.data

                // Check if result is base64 content (for download)
                const resultStr = typeof event.data === 'string' ? event.data :
                    (event.data.result ? (typeof event.data.result === 'string' ? event.data.result : null) : null)

                if (resultStr && resultStr.length > 100) {
                    // Likely base64 content
                    currentRun.value.hasDownload = true
                }
            }

            // Fetch costs from status API after a short delay
            // The delay ensures the Celery task has fully completed and costs are available
            if (currentRun.value.taskId) {
                const taskId = currentRun.value.taskId
                setTimeout(() => {
                    getWorkflowStatus(taskId).catch(console.error)
                }, 3000) // 3 second delay to ensure task completion
            }
        } else if (eventType === 'workflow_error' || eventType.includes('workflow_fail') || eventType.includes('workflow_error')) {
            currentRun.value.status = 'error'
            currentRun.value.completedAt = new Date()
            const workflowError = event.data?.error || event.message
            if (workflowError) {
                currentRun.value.error = workflowError
            }
            finalizeWorkflowNodeStates('error', event.timestamp, workflowError)

            // Also fetch costs on error (partial costs) with delay
            if (currentRun.value.taskId) {
                const taskId = currentRun.value.taskId
                setTimeout(() => {
                    getWorkflowStatus(taskId).catch(console.error)
                }, 3000)
            }
        }
    }

    // ==================== DOWNLOAD ====================

    const downloadResult = async (): Promise<void> => {
        if (!currentRun.value?.taskId) {
            throw new Error('No active workflow run')
        }

        try {
            let base64Content: string | null = null

            // First try to get result from currentRun (received via WebSocket)
            if (currentRun.value.result) {
                base64Content = extractBase64Content(currentRun.value.result)
            }

            // Fallback: fetch from status endpoint
            if (!base64Content) {
                const status = await getWorkflowStatus(currentRun.value.taskId)

                if (status.state !== 'SUCCESS') {
                    throw new Error(`Workflow not completed. State: ${status.state}`)
                }

                if (!status.info) {
                    throw new Error('No result available')
                }

                base64Content = extractBase64Content(status.info)
            }

            if (!base64Content) {
                throw new Error('No downloadable content found in result')
            }

            // Decode base64 and create download
            downloadBase64AsFile(base64Content, currentRun.value.taskId)
        } catch (error) {
            console.error('Download failed:', error)
            throw error
        }
    }

    const extractBase64Content = (result: any): string | null => {
        if (typeof result === 'string' && result.length > 100) {
            return result
        }

        if (typeof result === 'object' && result !== null) {
            // Check for status wrapper (from ExecWorkflowSuccess)
            const innerResult = result.status === 'success' ? result.result : result

            if (typeof innerResult === 'string' && innerResult.length > 100) {
                return innerResult
            }

            if (typeof innerResult === 'object' && innerResult !== null) {
                const content = innerResult.content || innerResult.base64 || innerResult.file || innerResult.data
                if (typeof content === 'string' && content.length > 100) {
                    return content
                }
            }
        }

        return null
    }

    const downloadBase64AsFile = (base64Content: string, fileNameSeed: string): void => {
        // Decode base64
        const binaryString = atob(base64Content)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
        }

        // Detect file type from magic bytes
        let mimeType = 'application/octet-stream'
        let extension = 'bin'

        const header = bytes.slice(0, 8)
        if (header[0] === 0x50 && header[1] === 0x4B && header[2] === 0x03 && header[3] === 0x04) {
            // PK header - ZIP/DOCX
            mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            extension = 'docx'
        } else if (header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46) {
            // %PDF header
            mimeType = 'application/pdf'
            extension = 'pdf'
        } else if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4E && header[3] === 0x47) {
            // PNG header
            mimeType = 'image/png'
            extension = 'png'
        }

        // Create blob and download
        const blob = new Blob([bytes], { type: mimeType })
        const url = window.URL.createObjectURL(blob)
        const sanitizedSeed = fileNameSeed
            .trim()
            .replace(/[<>:"/\\|?*\x00-\x1F]+/g, '_')
            .replace(/\s+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '')
        const filenameBase = sanitizedSeed || 'workflow_result'
        const filename = `${filenameBase}.${extension}`

        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    // ==================== UTILITIES ====================

    const disconnect = () => {
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval)
            heartbeatInterval = null
        }
        if (websocket.value) {
            websocket.value.close(1000, 'User disconnected')
            websocket.value = null
        }
        isConnected.value = false
    }

    const clearEvents = () => {
        events.value = []
        if (currentRun.value) currentRun.value.events = []
    }

    const resetRun = () => {
        disconnect()
        currentRun.value = null
        events.value = []
        connectionError.value = null
    }

    const selectWorkflow = (workflowId: number | null) => {
        currentWorkflowId.value = workflowId
        selectedNodeId.value = null
        resetRun()

        // Persist selected workflow per app in localStorage
        if (workflowId !== null && guid.value) {
            localStorage.setItem(`workflow_selected_${guid.value}`, String(workflowId))
        } else if (guid.value) {
            localStorage.removeItem(`workflow_selected_${guid.value}`)
        }
    }

    /**
     * Restore the previously selected workflow for the current app from localStorage.
     * Falls back to the first workflow if stored ID is not found.
     */
    const restoreSelectedWorkflow = () => {
        const storedId = guid.value ? localStorage.getItem(`workflow_selected_${guid.value}`) : null
        if (storedId) {
            const id = Number(storedId)
            const exists = workflows.value.some(w => w.id === id)
            if (exists) {
                selectWorkflow(id)
                return
            }
        }
        // Fallback: select first workflow
        if (workflows.value.length > 0) {
            selectWorkflow(workflows.value[0].id!)
        } else {
            selectWorkflow(null)
        }
    }

    const selectNode = (nodeId: string | null) => {
        selectedNodeId.value = nodeId
    }

    const toggleEditMode = () => {
        editMode.value = !editMode.value
    }

    // Helper: create default node configs
    const createDefaultNode = (type: NodeType, id: string, name?: string): WorkflowNode => {
        // Use provided name or generate a sensible default based on type and id
        const defaultNames: Record<NodeType, string> = {
            'input': t('workflowEditor.nodeDefaults.input'),
            'agent': t('workflowEditor.nodeDefaults.agent'),
            'join': t('workflowEditor.nodeDefaults.join'),
            'pause': t('workflowEditor.nodeDefaults.pause'),
            'base64': t('workflowEditor.nodeDefaults.base64'),
            'excel': t('workflowEditor.nodeDefaults.excel'),
            'ocr': t('workflowEditor.nodeDefaults.ocr'),
            'email': t('workflowEditor.nodeDefaults.email'),
            'knowledge_base': t('workflowEditor.nodeDefaults.knowledge_base'),
            'ingestion': t('workflowEditor.nodeDefaults.ingestion')
        }
        const nodeName = name || defaultNames[type] || `${type.charAt(0).toUpperCase() + type.slice(1)} Node`
        const base = { id, name: nodeName, node_type: type }
        switch (type) {
            case 'input':
                return { ...base, node_type: 'input', content: '', return_type: 'auto' } as InputNodeConfig
            case 'agent': {
                // Auto-create LLM config with the first available model
                const firstModel = availableModelsInfo.value?.[0]
                const llmConfig = firstModel
                    ? {
                        provider_config: {
                            cloud_provider: firstModel.cloud_provider || 'azure',
                            model_provider: firstModel.model_provider || 'open_ai',
                            prompt: 'You are a helpful assistant.',
                            params: {
                                model: firstModel.name,
                                temperature: 1,
                                max_tokens: null,
                                top_p: 1.0,
                                timeout: 300,
                                presence_penalty: 0,
                                frequency_penalty: 0
                            }
                        },
                        retries: 10,
                        output_retries: 10,
                        streaming: false,
                        mcp_servers: [],
                        available_tools: []
                    }
                    : ''  // Fallback – should not happen since caller checks
                return {
                    ...base,
                    node_type: 'agent',
                    llm_config: llmConfig,
                    question: '${inputs}',
                    role: 'assistant',
                    description: ''
                } as AgentNodeConfig
            }
            case 'join':
                return {
                    ...base,
                    node_type: 'join',
                    parameters: { reducer: 'list_append', initial_factory: 'list' }
                } as JoinNodeConfig
            case 'pause':
                return {
                    ...base,
                    node_type: 'pause',
                    duration_seconds: 1,
                    description: ''
                } as PauseNodeConfig
            case 'base64':
                return { ...base, node_type: 'base64', file_type: 'docx', content: '', css: '' } as Base64NodeConfig
            case 'excel':
                return {
                    ...base,
                    node_type: 'excel',
                    sheets: [{
                        name: 'Sheet1',
                        headers: ['Column1'],
                        data: ['${state.nodeName.field}'],  // Array of data source references per column
                        freeze_header: true,
                        header_style: undefined
                    }]
                } as ExcelNodeConfig
            case 'ocr':
                return {
                    ...base,
                    node_type: 'ocr',
                    description: '',
                    file_base64: '${state.inputNode.file}',
                    file_type: 'pdf',
                    pdf_mode: 'extract',
                    output_format: 'text',
                    include_metadata: false,
                    compact_metadata: false,
                    metadata_fields: [],
                    config: {
                        cloud_provider: 'azure',
                        doc_analysis_provider: 'azure_doc_intel',
                        params: {}
                    }
                } as OcrNodeConfig
            case 'email':
                return {
                    ...base,
                    node_type: 'email',
                    subject: '',
                    html_body: '',
                    plain_text_body: '',
                    to: [{ address: '', displayName: '' }],
                    wait_for_completion: true,
                    timeout_seconds: 60
                } as EmailNodeConfig
            case 'knowledge_base':
                return {
                    ...base,
                    node_type: 'knowledge_base',
                    description: '',
                    query: '${inputs}',
                    index_name: '',
                    embedding_model_id: 0,
                    vectorstore_config_id: 0,
                    top_k: 5,
                    search_type: 'hybrid',
                    score_threshold: null
                } as KnowledgeBaseNodeConfig
            case 'ingestion':
                return {
                    ...base,
                    node_type: 'ingestion',
                    description: '',
                    content: '${inputs}',
                    index_name: '',
                    chunk_size: 800,
                    chunk_overlap: 400
                } as IngestionNodeConfig
            default:
                return { ...base, node_type: 'input', return_type: 'auto' } as InputNodeConfig
        }
    }

    const loadExecutionHistory = handleAxiosError(
        rootAuth(async (workflowId: number, query: WorkflowExecutionHistoryQuery = {}): Promise<void> => {
            isLoadingHistory.value = true
            try {
                executionHistoryQuery.value = {
                    startedFrom: query.startedFrom ?? null,
                    startedTo: query.startedTo ?? null,
                    limit: query.limit ?? null,
                    offset: query.offset ?? 0,
                }

                const params: Record<string, string | number> = {}
                if (executionHistoryQuery.value.limit != null) {
                    params.limit = executionHistoryQuery.value.limit
                }
                if ((executionHistoryQuery.value.offset ?? 0) > 0) {
                    params.offset = executionHistoryQuery.value.offset ?? 0
                }
                if (executionHistoryQuery.value.startedFrom) {
                    params.started_from = executionHistoryQuery.value.startedFrom
                }
                if (executionHistoryQuery.value.startedTo) {
                    params.started_to = executionHistoryQuery.value.startedTo
                }

                const { data } = await axios.get<WorkflowExecution[]>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/executions`,
                    {
                        ...axiosBearerHeader.value,
                        params,
                    }
                )
                executionHistory.value = data
            } finally {
                isLoadingHistory.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.history.error' }
    )

    const loadExecutionSnapshots = handleAxiosError(
        rootAuth(async (workflowId: number, executionId: number): Promise<void> => {
            isLoadingSnapshots.value = true
            try {
                const { data } = await axios.get<NodeSnapshot[]>(
                    `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/executions/${executionId}/snapshots`,
                    axiosBearerHeader.value
                )
                executionSnapshots.value = data
            } finally {
                isLoadingSnapshots.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.snapshots.error' }
    )

    const cancelWorkflow = handleAxiosError(
        rootAuth(async (taskId: string): Promise<boolean> => {
            const activeRun = currentRun.value?.taskId === taskId ? currentRun.value : null
            const previousStatus = activeRun?.status
            const previousCompletedAt = activeRun?.completedAt

            if (activeRun) {
                activeRun.status = 'cancelled'
                activeRun.completedAt = new Date()
            }

            try {
                await axios.post(
                    `/${guid.value}/graph/${taskId}/cancel`,
                    {},
                    axiosBearerHeader.value
                )
            } catch (error: any) {
                // 409 means the task already reached a terminal state.
                if (error?.response?.status !== 409) {
                    if (activeRun && previousStatus) {
                        activeRun.status = previousStatus
                        activeRun.completedAt = previousCompletedAt
                    }
                    throw error
                }
            }

            if (currentRun.value?.taskId === taskId) {
                disconnect()
            }
            return true
        }, false),
        { errorMsg: 'stores.workflow.cancel.error' }
    )

    const resumeWorkflow = handleAxiosError(
        rootAuth(async (workflowId: number, executionId: number): Promise<string | null> => {
            const { data } = await axios.post<{ status: string; task_id: string; original_run_id: string }>(
                `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/executions/${executionId}/resume`,
                {},
                axiosBearerHeader.value
            )

            const taskId = data.task_id
            const workflow = workflows.value.find(w => w.id === workflowId)

            // Initialize current run for the resumed workflow
            const nodeStatusMap = new Map<string, NodeStatus>()
            if (workflow) {
                workflow.nodes.forEach(node => {
                    nodeStatusMap.set(node.id, 'pending')
                })
            }

            currentRun.value = {
                taskId,
                workflowId,
                workflowName: workflow?.name || `Workflow ${workflowId}`,
                status: 'pending',
                startedAt: new Date(),
                events: [],
                nodeStatuses: nodeStatusMap
            }

            nodeExecutionCounts.value.clear()
            nodeEventData.value.clear()
            nodeRunningIterations.value.clear()

            await connectWebSocket(taskId)
            return taskId
        }, false),
        { errorMsg: 'stores.workflow.resume.error' }
    )

    const cancelStuckExecution = handleAxiosError(
        rootAuth(async (workflowId: number, executionId: number): Promise<void> => {
            await axios.post(
                `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/executions/${executionId}/cancel`,
                {},
                axiosBearerHeader.value
            )
            // Refresh the history list so the UI reflects the new status
            await loadExecutionHistory(workflowId, executionHistoryQuery.value)
        }, false),
        { errorMsg: 'stores.workflow.cancelStuck.error' }
    )

    const rerunExecution = handleAxiosError(
        rootAuth(async (workflowId: number, executionId: number): Promise<string | null> => {
            const { data } = await axios.post<{ status: string; task_id: string; original_execution_id: number }>(
                `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/executions/${executionId}/rerun`,
                {},
                axiosBearerHeader.value
            )

            const taskId = data.task_id
            const workflow = workflows.value.find(w => w.id === workflowId)

            const nodeStatusMap = new Map<string, NodeStatus>()
            if (workflow) {
                workflow.nodes.forEach(node => {
                    nodeStatusMap.set(node.id, 'pending')
                })
            }

            currentRun.value = {
                taskId,
                workflowId,
                workflowName: workflow?.name || `Workflow ${workflowId}`,
                status: 'pending',
                startedAt: new Date(),
                events: [],
                nodeStatuses: nodeStatusMap
            }

            nodeExecutionCounts.value.clear()
            nodeEventData.value.clear()
            nodeRunningIterations.value.clear()

            await connectWebSocket(taskId)
            return taskId
        }, false),
        { errorMsg: 'stores.workflow.rerun.error' }
    )

    // Helper: get targets from next_node
    const getNextNodeTargets = (nextNode: NextNode | undefined): string[] => {
        if (!nextNode) return []
        if (typeof nextNode === 'string') return [nextNode]
        if ('target' in nextNode) return [(nextNode as EdgeConfig).target]
        if (Array.isArray(nextNode)) return nextNode.map((c: ConditionT) => c.target)
        if ('targets' in nextNode) return (nextNode as ParallelT).targets
        return []
    }

    // ==================== WORKFLOW APPROVAL ====================

    const loadApprovalStatuses = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            const { data } = await axios.get<ApprovalStatus[]>(
                `${portalUrl.value}/${guid.value}/workflow-approvals/all`,
                axiosBearerHeader.value
            )
            approvalStatuses.value = data
        }, false),
        { errorMsg: 'stores.workflow.approval.loadStatuses.error' }
    )

    const loadApproval = handleAxiosError(
        rootAuth(async (workflowId: number): Promise<void> => {
            const { data } = await axios.get<WorkflowApproval | null>(
                `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/approval`,
                axiosBearerHeader.value
            )
            currentApproval.value = data
        }, false),
        { errorMsg: 'stores.workflow.approval.load.error' }
    )

    const submitApproval = handleAxiosError(
        rootAuth(async (workflowId: number, payload: SubmitApprovalPayload): Promise<void> => {
            const { data } = await axios.post<WorkflowApproval>(
                `${portalUrl.value}/${guid.value}/my-workflows/${workflowId}/approval/request`,
                payload,
                axiosBearerHeader.value
            )
            currentApproval.value = data
            await loadApprovalStatuses()
        }, false),
        { errorMsg: 'stores.workflow.approval.submit.error' }
    )

    const loadAllApprovals = handleAxiosError(
        rootAuth(async (status?: string): Promise<void> => {
            isLoadingApprovals.value = true
            try {
                const url = status
                    ? `${portalUrl.value}/${guid.value}/workflow-approvals?status=${status}`
                    : `${portalUrl.value}/${guid.value}/workflow-approvals`
                const { data } = await axios.get<WorkflowApproval[]>(url, axiosBearerHeader.value)
                allApprovals.value = data
            } finally {
                isLoadingApprovals.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.approval.loadAll.error' }
    )

    const reviewApproval = handleAxiosError(
        rootAuth(async (approvalId: number, payload: ReviewApprovalPayload): Promise<void> => {
            const { data } = await axios.put<WorkflowApproval>(
                `${portalUrl.value}/${guid.value}/workflow-approvals/${approvalId}/review`,
                payload,
                axiosBearerHeader.value
            )
            currentApproval.value = data
            const idx = allApprovals.value.findIndex(a => a.id === approvalId)
            if (idx >= 0) allApprovals.value[idx] = data
            await loadApprovalStatuses()
        }, false),
        { errorMsg: 'stores.workflow.approval.review.error' }
    )

    const revokeApproval = handleAxiosError(
        rootAuth(async (approvalId: number, reason: string): Promise<void> => {
            const { data } = await axios.put<WorkflowApproval>(
                `${portalUrl.value}/${guid.value}/workflow-approvals/${approvalId}/revoke`,
                { reason },
                axiosBearerHeader.value
            )
            currentApproval.value = data
            const idx = allApprovals.value.findIndex(a => a.id === approvalId)
            if (idx >= 0) allApprovals.value[idx] = data
            await loadApprovalStatuses()
        }, false),
        { errorMsg: 'stores.workflow.approval.revoke.error' }
    )

    const getApprovalStatus = (workflowId: number): ApprovalStatus | undefined => {
        return approvalStatuses.value.find(a => a.workflow_id === workflowId)
    }

    const isWorkflowApproved = (workflowId: number): boolean => {
        const status = getApprovalStatus(workflowId)
        if (!status || status.status !== 'approved') return false
        if (status.approved_until && new Date(status.approved_until) < new Date()) return false
        return true
    }

    // ==================== APPROVAL SETTINGS ====================

    const loadApprovalConfig = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            isLoadingApprovalConfig.value = true
            try {
                const { data } = await axios.get<ApprovalConfig>(
                    `${portalUrl.value}/${guid.value}/workflow-approval-settings`,
                    axiosBearerHeader.value
                )
                approvalConfig.value = data
            } finally {
                isLoadingApprovalConfig.value = false
            }
        }, false),
        { errorMsg: 'stores.workflow.approvalConfig.load.error' }
    )

    const updateApprovalConfig = handleAxiosError(
        rootAuth(async (payload: UpdateApprovalConfigPayload): Promise<void> => {
            isLoadingApprovalConfig.value = true
            try {
                const { data } = await axios.put<ApprovalConfig>(
                    `${portalUrl.value}/${guid.value}/workflow-approval-settings`,
                    payload,
                    axiosBearerHeader.value
                )
                approvalConfig.value = data
            } finally {
                isLoadingApprovalConfig.value = false
            }
        }, false),
        {
            errorMsg: 'stores.workflow.approvalConfig.update.error',
            successMsg: 'stores.workflow.approvalConfig.update.success'
        }
    )

    return {
        // State
        workflows,
        currentWorkflowId,
        currentRun,
        isConnected,
        events,
        connectionError,
        isLoading,
        isSaving,
        selectedNodeId,
        editMode,
        nodeExecutionCounts,
        nodeEventData,
        availableModels,
        availableModelsInfo,
        assistantPreview,
        assistantPreviewNodePositions,
        assistantRecentChanges,
        assistantPendingChanges,
        assistantFocusedMessageId,
        pendingCanvasNodePositions,

        // Computed
        nodeStatuses,
        currentWorkflow,
        selectedNode,

        // Workflow CRUD
        loadWorkflows,
        loadWorkflow,
        resolveWorkflowApp,
        createWorkflow,
        updateWorkflow,
        updateWorkflowSkill,
        deleteWorkflow,
        copyWorkflow,
        publishWorkflow,
        loadAvailableModels,

        // Node operations
        addNode,
        replaceCurrentWorkflow,
        updateNode,
        setStartNode,
        removeNode,
        connectNodes,
        disconnectNodes,
        selectNode,
        createDefaultNode,
        setAssistantPreview,
        clearAssistantPreview,
        setAssistantPreviewNodePosition,
        queuePendingCanvasNodePosition,
        takePendingCanvasNodePositions,
        flashAssistantRecentChanges,
        clearAssistantRecentChanges,
        setAssistantPendingChanges,
        clearAssistantPendingChanges,
        setAssistantFocusedMessage,

        // Workflow execution
        startWorkflow,
        getWorkflowStatus,
        connectWebSocket,
        downloadResult,
        cancelWorkflow,
        resumeWorkflow,
        cancelStuckExecution,
        rerunExecution,

        // Execution history
        executionHistory,
        isLoadingHistory,
        loadExecutionHistory,
        executionSnapshots,
        isLoadingSnapshots,
        loadExecutionSnapshots,

        // Utilities
        disconnect,
        clearEvents,
        resetRun,
        selectWorkflow,
        restoreSelectedWorkflow,
        toggleEditMode,
        getNextNodeTargets,
        extractBase64Content,
        downloadBase64AsFile,

        // Approval
        approvalStatuses,
        currentApproval,
        allApprovals,
        isLoadingApprovals,
        loadApprovalStatuses,
        loadApproval,
        submitApproval,
        loadAllApprovals,
        reviewApproval,
        revokeApproval,
        getApprovalStatus,
        isWorkflowApproved,

        // Approval Settings
        approvalConfig,
        isLoadingApprovalConfig,
        loadApprovalConfig,
        updateApprovalConfig
    }
})
